import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSelector } from 'react-redux';
import { loadRoles } from '../features/roles/rolesSlice';
import { loadUsers } from '../features/users/usersSlice';
import { selectCurrentUser } from '../features/auth/authSlice';
import { RootState } from '../store/store';
import { useAppDispatch } from '../store/hooks';
import { RoleMultiSelect } from './RoleMultiSelect';
import { UserGroupMultiSelect } from './UserGroupMultiSelect';
import { RequiredFieldsLegend } from './RequiredFieldsLegend';

const schema = z.object({
  title: z.string().min(1, 'El título es requerido').max(255, 'El título no puede exceder 255 caracteres'),
  description: z.string().optional(),
  type: z.enum(['periodic', 'permanent'], { required_error: 'Selecciona un tipo' }),
  periodType: z.enum(['mensual', 'semestral', 'anual']).optional().nullable(),
  seriesId: z.string().max(120).optional().nullable(),
  periodLabel: z.string().max(64).optional().nullable(),
  periodDate: z.string().optional().nullable(),
  threshold: z.preprocess(
    (val) => (val === '' || val === null || val === undefined ? null : Number(val)),
    z.number().min(0).max(10).nullable().optional(),
  ),
  visibleRoles: z.array(z.string()).optional(),
  editableRoles: z.array(z.string()).optional(),
  visibleGroupIds: z.array(z.string()).optional(),
  editableGroupIds: z.array(z.string()).optional(),
  createdById: z.number().optional(),
  status: z.enum(['draft', 'published']).optional(),
}).refine((data) => {
  if (data.type === 'periodic' && !data.periodType) {
    return false;
  }
  return true;
}, {
  message: 'Selecciona un período para encuestas periódicas',
  path: ['periodType'],
});

type FormValues = z.infer<typeof schema>;

interface Props {
  initialValues?: Partial<
    FormValues & {
      visibleRoles?: string[];
      editableRoles?: string[];
      visibleGroupIds?: string[];
      editableGroupIds?: string[];
      createdById?: number;
    }
  > | null;
  onSubmit: (values: FormValues) => void;
  onCancel?: () => void;
  error?: string;
}

export function SurveysForm({ initialValues, onSubmit, onCancel, error }: Props) {
  const dispatch = useAppDispatch();
  const { items: roles, loading: rolesLoading } = useSelector((state: RootState) => state.roles);
  const { items: users, loading: usersLoading } = useSelector((state: RootState) => state.users);
  const currentUser = useSelector(selectCurrentUser);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [selectedEditableRoles, setSelectedEditableRoles] = useState<string[]>([]);
  const [selectedVisibleGroupIds, setSelectedVisibleGroupIds] = useState<string[]>([]);
  const [selectedEditableGroupIds, setSelectedEditableGroupIds] = useState<string[]>([]);
  const [selectedOwnerId, setSelectedOwnerId] = useState<number | undefined>(undefined);
  const isSuperadmin = currentUser?.role === 'superadmin';
  const ownerForGroups = selectedOwnerId ?? currentUser?.id;

  useEffect(() => {
    // Cargar roles y usuarios para los selectores
    dispatch(loadRoles({ pageSize: 200 }));
    if (isSuperadmin) {
      dispatch(loadUsers({ pageSize: 200 }));
    }
  }, [dispatch, isSuperadmin]);

  useEffect(() => {
    // Inicializar roles seleccionados y dueño desde initialValues
    if (initialValues?.visibleRoles) {
      setSelectedRoles(initialValues.visibleRoles);
    }
    if (initialValues?.editableRoles) {
      setSelectedEditableRoles(initialValues.editableRoles);
    }
    if (initialValues?.visibleGroupIds) {
      setSelectedVisibleGroupIds(initialValues.visibleGroupIds);
    }
    if (initialValues?.editableGroupIds) {
      setSelectedEditableGroupIds(initialValues.editableGroupIds);
    }
    if (initialValues?.createdById) {
      setSelectedOwnerId(initialValues.createdById);
    } else if (!initialValues && currentUser?.id) {
      // Si es creación nueva y no hay initialValues, usar el usuario actual como dueño por defecto
      setSelectedOwnerId(currentUser.id);
    }
  }, [initialValues, currentUser]);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: zodResolver(schema) as Resolver<FormValues>,
    defaultValues: initialValues ?? { 
      title: '', 
      description: '', 
      type: 'permanent', 
      periodType: null,
      threshold: null,
      visibleRoles: [],
      visibleGroupIds: [],
      editableGroupIds: [],
      createdById: currentUser?.id
    }
  });

  const surveyType = watch('type');

  // Sincronizar selectedRoles y selectedOwnerId con el formulario
  useEffect(() => {
    setValue('visibleRoles', selectedRoles);
  }, [selectedRoles, setValue]);

  useEffect(() => {
    setValue('editableRoles', selectedEditableRoles);
  }, [selectedEditableRoles, setValue]);

  useEffect(() => {
    setValue('visibleGroupIds', selectedVisibleGroupIds);
  }, [selectedVisibleGroupIds, setValue]);

  useEffect(() => {
    setValue('editableGroupIds', selectedEditableGroupIds);
  }, [selectedEditableGroupIds, setValue]);

  useEffect(() => {
    if (selectedOwnerId !== undefined) {
      setValue('createdById', selectedOwnerId);
    }
  }, [selectedOwnerId, setValue]);

  useEffect(() => {
    if (initialValues) {
      const periodDateValue = initialValues.periodDate
        ? String(initialValues.periodDate).slice(0, 10)
        : null;
      reset({
        ...initialValues,
        periodDate: periodDateValue,
        visibleRoles: initialValues.visibleRoles || [],
        editableRoles: initialValues.editableRoles || [],
        visibleGroupIds: initialValues.visibleGroupIds || [],
        editableGroupIds: initialValues.editableGroupIds || [],
        createdById: initialValues.createdById,
      } as FormValues);
      if (initialValues.visibleRoles) {
        setSelectedRoles(initialValues.visibleRoles);
      }
      if (initialValues.editableRoles) {
        setSelectedEditableRoles(initialValues.editableRoles);
      }
      setSelectedVisibleGroupIds(initialValues.visibleGroupIds || []);
      setSelectedEditableGroupIds(initialValues.editableGroupIds || []);
      if (initialValues.createdById) {
        setSelectedOwnerId(initialValues.createdById);
      }
    } else {
      reset({ 
        title: '', 
        description: '', 
        type: 'permanent', 
        periodType: null,
        threshold: null,
        visibleRoles: [],
        editableRoles: [],
        visibleGroupIds: [],
        editableGroupIds: [],
        createdById: currentUser?.id
      });
      setSelectedRoles([]);
      setSelectedEditableRoles([]);
      setSelectedVisibleGroupIds([]);
      setSelectedEditableGroupIds([]);
      setSelectedOwnerId(currentUser?.id);
    }
  }, [initialValues, reset, currentUser]);

  const isEditing = !!initialValues;

  return (
    <form className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Título *</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground"
          type="text"
          placeholder="Ej: Encuesta de Satisfacción del Cliente"
          {...register('title')}
        />
        {errors.title && <p className="text-sm text-destructive">{errors.title.message}</p>}
        {!errors.title && error && <p className="text-sm text-destructive">{error}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Descripción</label>
        <textarea
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground placeholder:text-muted-foreground min-h-[100px]"
          placeholder="Descripción de la encuesta..."
          {...register('description')}
        />
        {errors.description && <p className="text-sm text-destructive">{errors.description.message}</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Tipo *</label>
        <select
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          {...register('type')}
        >
          <option value="permanent">Permanente</option>
          <option value="periodic">Periódica</option>
        </select>
        {errors.type && <p className="text-sm text-destructive">{errors.type.message}</p>}
        <p className="text-xs text-muted-foreground mt-1">
          Permanente: se puede responder en cualquier momento. Periódica: se realiza en períodos específicos y permite comparación temporal.
        </p>
      </div>

      {surveyType === 'periodic' && (
        <>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Período *</label>
            <select
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
              {...register('periodType')}
            >
              <option value="">Selecciona un período...</option>
              <option value="mensual">Mensual</option>
              <option value="semestral">Semestral</option>
              <option value="anual">Anual</option>
            </select>
            {errors.periodType && <p className="text-sm text-destructive">{errors.periodType.message}</p>}
            <p className="text-xs text-muted-foreground mt-1">
              Define la frecuencia de la encuesta para comparaciones históricas.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Serie (identificador lógico)</label>
            <input
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
              type="text"
              placeholder="Ej: satisfaccion-cliente-2025"
              {...register('seriesId')}
            />
            <p className="text-xs text-muted-foreground mt-1">
              Encuestas con la misma serie forman una línea de tiempo por período declarado.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Etiqueta de período</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                type="text"
                placeholder="Ej: 2025-S1, 2025-03"
                {...register('periodLabel')}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Fecha canónica del período</label>
              <input
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
                type="date"
                {...register('periodDate')}
              />
            </div>
          </div>
        </>
      )}

      <div>
        <label className="block text-sm font-medium text-foreground mb-1">Umbral mínimo (opcional)</label>
        <input
          className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
          type="number"
          step="0.01"
          min="0"
          max="10"
          placeholder="Ej: 7.0"
          {...register('threshold')}
        />
        {errors.threshold && <p className="text-sm text-destructive">{String(errors.threshold.message)}</p>}
        <p className="text-xs text-muted-foreground mt-1">
          Respuestas con puntaje total por debajo de este valor se marcarán como bajo umbral. También puede definir umbrales por agrupación.
        </p>
      </div>

      {isSuperadmin && (
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Dueño de la Encuesta</label>
          <select
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none text-foreground"
            value={selectedOwnerId || ''}
            onChange={(e) => {
              const ownerId = e.target.value ? parseInt(e.target.value) : undefined;
              setSelectedOwnerId(ownerId);
              setValue('createdById', ownerId);
            }}
            disabled={usersLoading}
          >
            <option value="">Selecciona un dueño...</option>
            {users.map((user) => (
              <option key={user.id} value={user.id}>
                {user.email} {user.role === 'superadmin' ? '(Superadmin)' : ''}
              </option>
            ))}
          </select>
          <p className="text-xs text-muted-foreground mt-1">
            El dueño podrá editar esta encuesta y gestionar sus formularios. El superadmin siempre puede editar cualquier encuesta.
          </p>
        </div>
      )}

      <div>
        <RoleMultiSelect
          roles={roles}
          selectedRoles={selectedRoles}
          onChange={setSelectedRoles}
          loading={rolesLoading}
          label="Roles con Visibilidad"
          placeholder="Selecciona los roles que pueden ver esta encuesta y sus respuestas (opcional)"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Los roles seleccionados podrán ver las respuestas y exportar a Excel de esta encuesta.
        </p>
      </div>

      <div>
        <RoleMultiSelect
          roles={roles}
          selectedRoles={selectedEditableRoles}
          onChange={setSelectedEditableRoles}
          loading={rolesLoading}
          label="Roles con Permiso de Edición (Teable)"
          placeholder="Selecciona los roles que pueden editar el formato en Teable (opcional)"
        />
        <p className="text-xs text-muted-foreground mt-1">
          Los roles seleccionados podrán cambiar colores, fuentes y agregar comentarios en la hoja de Teable.
        </p>
      </div>

      <UserGroupMultiSelect
        label="Grupos con visibilidad"
        selectedIds={selectedVisibleGroupIds}
        onChange={setSelectedVisibleGroupIds}
        ownerUserId={ownerForGroups}
        placeholder="Grupos que pueden ver respuestas y estadísticas"
      />

      <UserGroupMultiSelect
        label="Grupos con edición (Teable / gestión)"
        selectedIds={selectedEditableGroupIds}
        onChange={setSelectedEditableGroupIds}
        ownerUserId={ownerForGroups}
        placeholder="Grupos con permiso de edición de formato en Teable"
      />

      <RequiredFieldsLegend />
      <div className="flex items-center gap-2">
        <button
          type="submit"
          className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          {isEditing ? 'Actualizar' : 'Crear'} Encuesta
        </button>
        {onCancel && (
          <button
            type="button"
            className="inline-flex items-center justify-center rounded-md px-4 py-2 text-sm font-medium bg-secondary text-secondary-foreground hover:bg-secondary/80 transition-colors"
            onClick={onCancel}
          >
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}






